import {
  canonicalJson,
  elementDigest,
  isBookkeepingOnly,
  isFrameElement,
  orderScene,
  slimTombstone,
  studentChangeVerdict,
} from "@canvaschool/contracts";

import type {
  StudentRefusal,
  CanonicalUpdate,
  CanvasMode,
  CanvasMutation,
  CanvasRepairAck,
  CanvasRepairRequest,
  CanvasVerifyAck,
  ErrorCode,
  MutationAck,
  SerializedExcalidrawElement,
} from "@canvaschool/contracts";

type SceneElement = SerializedExcalidrawElement;

/** Far below anything a pointer can do; floats from the shift itself. */
const POINT_EPSILON = 1e-6;

/** Whether two copies of a line or arrow put every point at the same canvas
 * position, however the points and x/y split it between them. */
const sameAbsolutePoints = (a: SceneElement, b: SceneElement): boolean => {
  const { points: pointsA, x: ax, y: ay } = a;
  const { points: pointsB, x: bx, y: by } = b;
  if (
    !Array.isArray(pointsA) ||
    !Array.isArray(pointsB) ||
    pointsA.length !== pointsB.length ||
    typeof ax !== "number" ||
    typeof ay !== "number" ||
    typeof bx !== "number" ||
    typeof by !== "number"
  ) {
    return false;
  }
  return pointsA.every((pointA, index) => {
    const pointB = pointsB[index];
    return (
      Array.isArray(pointA) &&
      Array.isArray(pointB) &&
      Math.abs(ax + Number(pointA[0]) - (bx + Number(pointB[0]))) <
        POINT_EPSILON &&
      Math.abs(ay + Number(pointA[1]) - (by + Number(pointB[1]))) <
        POINT_EPSILON
    );
  });
};

export type SaveStatus =
  | "saved"
  | "saving"
  | "retrying"
  | "readOnly"
  | "revoked";

export type SyncNotice =
  | "EPOCH_DISCARDED"
  | "CHANGES_REJECTED"
  | "READ_ONLY_DISCARDED";

export type CanonicalScene = {
  epoch: string;
  version: number;
  scene: readonly SceneElement[];
  mode: CanvasMode;
};

/** The editor scene as the adapter sees it. */
export type SyncScene = {
  getElements(): readonly SceneElement[];
  /** The scene as the editor's onChange would hand it to `localChange`
   * (for example without an image still awaiting its placement click);
   * defaults to `getElements`. */
  getEditable?(): readonly SceneElement[];
  /** Replaces the whole scene, for a new epoch. */
  replace(elements: readonly SceneElement[]): void;
  /** Overwrites these elements locally, whatever their local versions.
   * `placeByIndex` also moves them to where their stacking index says. */
  restore(elements: readonly SceneElement[], placeByIndex?: boolean): void;
  /** Merges canonical elements into the scene without local history. */
  reconcile(elements: readonly SceneElement[]): void;
  /** Sets these elements' revision fields in place, leaving the rest. An
   * entry with an `index` also takes that stacking index and its place. */
  adoptRevisions(
    revisions: ReadonlyArray<
      Pick<SceneElement, "id" | "version" | "versionNonce" | "updated"> & {
        index?: string;
      }
    >,
  ): void;
};

export type CanvasSyncOptions = {
  classId: string;
  canvasId: string;
  /** Identifies this editor instance to the server. */
  clientId: string;
  initial: CanonicalScene;
  scene: SyncScene;
  send(mutation: CanvasMutation): Promise<MutationAck>;
  /** Image elements are sent only once their file is stored. */
  isFileReady(fileId: string): boolean;
  /**
   * Set for a student: local edits the shared student rule refuses are never
   * sent and snap back on screen. Omitted for a teacher, who is unrestricted.
   */
  student?: {
    studentNumber: number | null;
    /** A presentation canvas (발표 캔버스): the rule closes only the frames'
     * shape and the slide limit. */
    presentation?: boolean;
  };
  /** A student's own edit (not the core's bookkeeping) was refused and
   * snapped back; the reason is for a short notice. */
  onRefused?(reason: StudentRefusal): void;
  onStatus(status: SaveStatus): void;
  onNotice(notice: SyncNotice): void;
  onAccessLost(code: ErrorCode): void;
  /** The session rotated or ended; reconnect with a fresh token. */
  onReauthenticate(): void;
  /** The epoch moved without a canonical scene; rejoin to load it. */
  onResyncRequired(): void;
  /**
   * Operator-facing diagnostic for a "saving"/"retrying" status that has
   * lingered: tells apart a dropped socket from a client that is behind on
   * version and must rejoin before it can resend. Purely informational; it
   * never affects `status()` or `onStatus`.
   */
  onDiagnostic?(reason: "reconnecting" | "version_gap"): void;
  /**
   * Figma-style divergence detection: asks the server for the scene hash it
   * holds at the version this client believes it is at. Omitted disables
   * verification entirely — nothing here is required for normal sync.
   */
  verify?(canvasId: string): Promise<CanvasVerifyAck>;
  /**
   * After a verified hash mismatch, asks for only the elements that differ
   * from this client's manifest (`canvas:repair`). Omitted, or rejected, a
   * mismatch rejoins for the whole scene as before.
   */
  repair?(request: CanvasRepairRequest): Promise<CanvasRepairAck>;
  /**
   * Throttled (about 20/min total, enforced here, not by the caller),
   * best-effort operator diagnostics — distinct from `onDiagnostic`, which
   * is local-only console information. `detail` mirrors the wire shape in
   * `CanvasDiagnosticSchema` minus `canvasId`, which the caller adds.
   */
  diagnostic?(
    kind: string,
    detail?: Record<string, string | number | boolean | null | string[]>,
  ): void;
  randomUuid?: () => string;
  randomNonce?: () => number;
  now?: () => number;
  /** Batches rapid edits; zero sends immediately. */
  sendDelayMilliseconds?: number;
  retryDelayMilliseconds?: (attempt: number) => number;
  setTimer?: (callback: () => void, milliseconds: number) => () => void;
  /** How often the verify-eligibility condition is polled while idle (so a
   * quiet canvas still gets checked); default 5s. Verification itself still
   * only runs at most once per 15s (or every 60s when the version hasn't
   * moved), stretched for a large scene — see `verifyIntervals`. */
  verifyPollMilliseconds?: number;
};

export type CanvasSync = {
  localChange(elements: readonly SceneElement[]): void;
  /** The latest revision the server holds or this client already queued;
   * undefined means the element was just created locally. */
  revisionOf(elementId: string): SceneElement | undefined;
  joined(result: CanonicalScene): void;
  disconnected(): void;
  canonical(update: CanonicalUpdate): void;
  /** Stops sending; unsaved changes stay reported as unsaved. */
  revoke(code: ErrorCode): void;
  hasUnsavedChanges(): boolean;
  status(): SaveStatus;
  dispose(): void;
  /** Read-only introspection for the chaos-harness debug hook
   * (`window.__canvaschoolDebug`); not used by any normal code path. */
  debug(): {
    epoch: string;
    version: number;
    pending: {
      buffer: number;
      splitQueue: number;
      inFlight: number;
      heldBack: number;
    };
    /** The same hex SHA-256 the server computes for `canvas:verify`, over
     * the scene this client currently believes is canonical. */
    knownDigest(): Promise<string>;
    /** Runs an out-of-band verification round right now, bypassing the
     * normal eligibility cooldowns; for the chaos harness only. */
    verifyNow(): Promise<CanvasVerifyAck | { error: string }>;
  };
};

/** The same mutation may succeed when resent under its ID. */
const RETRYABLE = new Set<ErrorCode>(["RATE_LIMITED", "PERSISTENCE_FAILED"]);
const REAUTHENTICATE = new Set<ErrorCode>(["UNAUTHENTICATED", "CSRF_INVALID"]);
/**
 * The server refused the content itself; resending it cannot help.
 *
 * FORBIDDEN belongs here: on a mutation it is the frame policy declining one
 * edit, and the student is still a member of the canvas. Losing access
 * arrives the other way round — a socket revoke or a failed join — and those
 * paths still end the session. Reading a refused edit as lost access left
 * the editor in view mode with no tools and no way back short of leaving the
 * canvas.
 */
const REJECTED = new Set<ErrorCode>([
  "VALIDATION_FAILED",
  "CONFLICT",
  "ASSET_FAILED",
  "FORBIDDEN",
]);

export const createUuid = (): string => {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16,
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** Nothing is drawn in it yet: a shape at the click before the drag, a
 * stroke of one point, a label before its first keystroke. Mirrors the
 * server's own guard (normalizationGuard.ts) and Excalidraw's
 * `isInvisiblySmallElement`. */
const isNothingYet = (element: SceneElement): boolean => {
  if (element.type === "text") {
    return typeof element.text !== "string" || element.text.trim() === "";
  }
  if (Array.isArray(element.points)) {
    return element.points.length < 2;
  }
  return element.width === 0 && element.height === 0;
};

const sameRevision = (left: SceneElement, right: SceneElement) =>
  left.version === right.version && left.versionNonce === right.versionNonce;

/**
 * A teacher's deletion carries `customData.canvaschool.deletedByTeacher` so
 * no student can bring it back (`isDeletedByTeacher`, read by the shared
 * student rule). `CanvasEditor`'s onChange applies that tag when it *sees*
 * an element flip to `isDeleted` — but a scene replacement (opening a file,
 * a template swap) can drop an element outright, without ever handing it to
 * onChange as `isDeleted: true`. The tombstone synthesized below, for a
 * tracked element that simply vanished, is the only place left to mark it.
 */
const withTeacherDeletionMark = (element: SceneElement): SceneElement => {
  const customData = {
    ...((element.customData as Record<string, unknown> | undefined) ?? {}),
  };
  const canvaschool = {
    ...((customData.canvaschool as Record<string, unknown> | undefined) ?? {}),
  };
  canvaschool.deletedByTeacher = true;
  customData.canvaschool = canvaschool;
  return {
    ...element,
    customData: customData as SceneElement["customData"],
  };
};

/** Mirrors the server's reconcile: newer version wins, ties keep lower nonce. */
const keepsExisting = (existing: SceneElement, incoming: SceneElement) =>
  existing.version > incoming.version ||
  (existing.version === incoming.version &&
    existing.versionNonce < incoming.versionNonce);

/** Whether a refused revision is something the student did — a new
 * element, a deletion, a move or resize — rather than the core re-deriving a
 * field on its own. Only these are worth telling the student about. */
const isOwnEdit = (before: SceneElement | undefined, after: SceneElement) =>
  before === undefined ||
  before.isDeleted !== after.isDeleted ||
  before.x !== after.x ||
  before.y !== after.y ||
  before.width !== after.width ||
  before.height !== after.height ||
  before.angle !== after.angle;

const defaultTimer = (callback: () => void, milliseconds: number) => {
  const handle = window.setTimeout(callback, milliseconds);
  return () => window.clearTimeout(handle);
};

const defaultRetryDelay = (attempt: number) =>
  Math.min(30_000, 1_000 * 2 ** attempt);

const toHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

/** Client-side diagnostic throttle: about 20 a minute, total, across every
 * kind — a misbehaving class must never turn into a diagnostics flood. */
const DIAGNOSTIC_WINDOW_MILLISECONDS = 60_000;
const DIAGNOSTIC_LIMIT_PER_WINDOW = 20;

/** Refusals (a locally-blocked edit, or a server FORBIDDEN) are common
 * one-at-a-time noise during normal use; this aggregates them into one
 * count per window instead of one diagnostic per element. */
const REFUSAL_FLUSH_MILLISECONDS = 5_000;

/** How soon a refused element the core was still changing is looked at
 * again (see `restoreUnchanged`). */
const RECHECK_MILLISECONDS = 300;

/** How long a "saving"/"retrying" status must persist before it is worth an
 * operator's attention — once per continuous episode, not on a timer. */
const STUCK_SAVING_MILLISECONDS = 30_000;

const DEFAULT_VERIFY_POLL_MILLISECONDS = 5_000;
/** Verification itself waits at least this long between rounds... */
const VERIFY_MIN_INTERVAL_MILLISECONDS = 15_000;
/** ...but runs at least this often even while the version never moves, as a
 * periodic sanity check on an otherwise idle canvas. */
const VERIFY_MAX_INTERVAL_MILLISECONDS = 60_000;
/** A forced resync (server wins outright, H8) is disruptive enough to limit
 * to once per window even if verification keeps finding a mismatch. */
const FORCED_RESYNC_MIN_INTERVAL_MILLISECONDS = 60_000;
/** Each round hashes the whole scene on the main thread. Past this many
 * elements the intervals above stretch in proportion, up to
 * `VERIFY_MAX_STRETCH` times: a 3,000-element class canvas was hashed every
 * 15s by each of twenty school tablets (live class, 10-26). */
const VERIFY_ELEMENTS_PER_STRETCH = 1_000;
const VERIFY_MAX_STRETCH = 4;
/** ...and a device slow enough that hashing takes a noticeable share of the
 * time between rounds waits longer still: a round may take at most 1%. */
const VERIFY_MAX_DUTY = 0.01;
/** A repair (`canvas:repair`) is tried once per window; a mismatch found
 * again within it means the repair did not converge, and the client
 * rejoins for the whole scene instead. */
const REPAIR_MIN_INTERVAL_MILLISECONDS = 5 * 60_000;

type InFlight = {
  mutation: CanvasMutation;
  elements: Map<string, SceneElement>;
  attempt: number;
};

/**
 * Tracks which local element revisions the server holds and sends the rest as
 * idempotent mutations, one at a time. Nothing counts as saved before its
 * durable acknowledgement.
 */
export const createCanvasSync = (options: CanvasSyncOptions): CanvasSync => {
  const randomUuid = options.randomUuid ?? createUuid;
  const randomNonce =
    options.randomNonce ?? (() => Math.floor(Math.random() * 2 ** 31));
  const now = options.now ?? Date.now;
  const setTimer = options.setTimer ?? defaultTimer;
  const sendDelay = options.sendDelayMilliseconds ?? 100;
  const retryDelay = options.retryDelayMilliseconds ?? defaultRetryDelay;

  let epoch = options.initial.epoch;
  let version = options.initial.version;
  let mode = options.initial.mode;
  /** The latest revision of each element the server is known to hold. */
  let known = new Map<string, SceneElement>();
  /** Local revisions waiting to be sent. */
  let buffer = new Map<string, SceneElement>();
  /** Halves of a refused batch, resent before the buffer. */
  let splitQueue: SceneElement[][] = [];
  let inFlight: InFlight | null = null;
  /** Image elements waiting for their file upload. */
  let heldBack = new Set<string>();
  /** Element IDs that have been in this editor's scene since the last
   * replacement; only these can be deleted by vanishing from it. */
  const shown = new Set<string>();
  /** Element IDs this editor itself created: the only ones whose "nothing
   * in it yet" state is this user's own doing rather than a peer's
   * work-in-progress arriving here. */
  const authored = new Set<string>();
  /** Element IDs whose deletion the server declined (see `handleAck`): an
   * empty one is not offered again, or the core tidying it away again would
   * send the same refused deletion over and over. */
  const deletionDeclined = new Set<string>();
  let joined = false;
  let revoked = false;
  let retrying = false;
  let disposed = false;
  let cancelSend: (() => void) | null = null;
  let cancelRetry: (() => void) | null = null;
  let lastStatus: SaveStatus | null = null;

  // --- Diagnostics (throttled) ---
  let diagnosticWindowStart = 0;
  let diagnosticCountInWindow = 0;
  const emitDiagnostic = (
    kind: string,
    detail?: Record<string, string | number | boolean | null | string[]>,
  ) => {
    if (!options.diagnostic) {
      return;
    }
    const nowMs = now();
    if (nowMs - diagnosticWindowStart >= DIAGNOSTIC_WINDOW_MILLISECONDS) {
      diagnosticWindowStart = nowMs;
      diagnosticCountInWindow = 0;
    }
    if (diagnosticCountInWindow >= DIAGNOSTIC_LIMIT_PER_WINDOW) {
      return;
    }
    diagnosticCountInWindow += 1;
    options.diagnostic(kind, detail);
  };

  // Keyed by element ID: a refused drag is re-judged on every scene change
  // (dozens a second), so summing per call reported one dragged frame as
  // 167 refusals (live class, 10-07).
  let refusedInWindow = new Map<string, string | null>();
  let cancelRefusalFlush: (() => void) | null = null;
  const noteRefusals = (
    ids: readonly string[],
    reasons: readonly (string | null)[] = [],
  ) => {
    if (ids.length === 0 || !options.diagnostic) {
      return;
    }
    ids.forEach((id, index) => refusedInWindow.set(id, reasons[index] ?? null));
    cancelRefusalFlush ??= setTimer(() => {
      cancelRefusalFlush = null;
      const detail: Record<string, number> = { count: refusedInWindow.size };
      for (const reason of refusedInWindow.values()) {
        if (reason) {
          detail[reason] = (detail[reason] ?? 0) + 1;
        }
      }
      refusedInWindow = new Map();
      emitDiagnostic("refusal_restore", detail);
    }, REFUSAL_FLUSH_MILLISECONDS);
  };

  let cancelStuckSaving: (() => void) | null = null;

  // --- Scene verification (Figma-style divergence detection) ---
  let verifyInFlight = false;
  let lastVerifyAttemptAt: number | null = null;
  let lastVerifiedVersion: number | null = null;
  let lastForcedResyncAt: number | null = null;
  /** How long the last hash took, from canonical text to digest. */
  let lastHashMilliseconds = 0;
  let repairInFlight = false;
  let lastRepairAt: number | null = null;
  /** While a repair is in flight: elements a broadcast or acknowledgement
   * updated after its manifest was taken, which its answer must not undo. */
  let touchedDuringRepair: Set<string> | null = null;
  /** Element objects in `known` are never mutated, so a digest computed once
   * holds for as long as the object is kept. */
  const digests = new WeakMap<SceneElement, string>();
  const digestOf = (element: SceneElement) => {
    let digest = digests.get(element);
    if (digest === undefined) {
      digest = elementDigest(element);
      digests.set(element, digest);
    }
    return digest;
  };
  /** Set when a forced resync is pending: the *next* same-epoch join result
   * must overwrite the local scene outright (server wins), not reconcile by
   * version like an ordinary join — see `maybeForceResync` (H8). */
  let forceServerWinsOnNextJoin = false;
  let cancelVerifyPoll: (() => void) | null = null;

  const remember = (scene: readonly SceneElement[]) => {
    known = new Map(scene.map((element) => [element.id, element]));
  };
  remember(options.initial.scene);

  const hasPendingWork = () =>
    inFlight !== null ||
    buffer.size > 0 ||
    splitQueue.length > 0 ||
    heldBack.size > 0;

  /** Elements with local work not yet acknowledged: a server copy that
   * overwrites the screen leaves these alone, and they are sent as usual. */
  const pendingIds = () =>
    new Set([
      ...buffer.keys(),
      ...splitQueue.flat().map((element) => element.id),
      ...(inFlight?.elements.keys() ?? []),
      ...heldBack,
    ]);

  /** The server copies worth writing over this screen's: a deleted element
   * whose full local copy, cut down as it would be sent (`slimTombstone`),
   * already is the server's copy stays as it is — overwriting it would only
   * leave this screen's undo a stroke cut to a few points. */
  const differingOnScreen = (elements: readonly SceneElement[]) => {
    const local = new Map(
      options.scene.getElements().map((element) => [element.id, element]),
    );
    return elements.filter((element) => {
      const current = local.get(element.id);
      return !(
        current?.isDeleted &&
        sameRevision(current, element) &&
        canonicalJson(slimTombstone(current)) === canonicalJson(element)
      );
    });
  };

  /** Whether the editor currently has anything sitting unsaved. */
  const hasUnsavedChanges = () => mode === "EDIT" && hasPendingWork();

  const status = (): SaveStatus => {
    if (revoked) {
      return "revoked";
    }
    if (mode === "READ_ONLY") {
      return "readOnly";
    }
    if (retrying) {
      return "retrying";
    }
    return hasPendingWork() ? "saving" : "saved";
  };

  const publish = () => {
    const next = status();
    if (next !== lastStatus) {
      lastStatus = next;
      options.onStatus(next);
    }
    // stuck_saving: once per continuous "not saved" episode, not on a
    // recurring timer — cancelled and re-armed as the episode starts/ends.
    if (options.diagnostic && (next === "saving" || next === "retrying")) {
      cancelStuckSaving ??= setTimer(() => {
        cancelStuckSaving = null;
        emitDiagnostic("stuck_saving", {
          buffer: buffer.size,
          splitQueue: splitQueue.length,
          inFlight: inFlight ? 1 : 0,
          heldBack: heldBack.size,
        });
      }, STUCK_SAVING_MILLISECONDS);
    } else if (cancelStuckSaving) {
      cancelStuckSaving();
      cancelStuckSaving = null;
    }
    // Cheap: `dueForVerify` short-circuits on `joined`/pending work/timing
    // before doing anything real, so calling it from every settle point
    // (rather than threading it through each one) is fine.
    maybeVerify();
  };

  const cancelTimers = () => {
    cancelSend?.();
    cancelSend = null;
    cancelRetry?.();
    cancelRetry = null;
  };

  const discardPendingWork = () => {
    cancelTimers();
    inFlight = null;
    buffer = new Map();
    splitQueue = [];
    heldBack = new Set();
    retrying = false;
  };

  const canSend = () => joined && !revoked && !disposed && mode === "EDIT";

  /**
   * Shows the given replacements for elements whose local copy is still the
   * revision that was sent; a newer local edit is already on its way.
   */
  const hasPendingRevision = (id: string) =>
    buffer.has(id) ||
    heldBack.has(id) ||
    inFlight?.elements.has(id) === true ||
    splitQueue.some((elements) =>
      elements.some((element) => element.id === id),
    );

  /**
   * Applying server data can leave z-order indices duplicated or out of
   * order, and the core then re-numbers elements, bumping their versions.
   * That repair is not an edit: sending it had every client re-number the
   * others' elements in a loop, each write a stale full copy that overwrote
   * real edits. So an element whose only difference from the server's
   * revision is that bookkeeping takes the server's revision back.
   *
   * It takes the server's index back too wherever that index is sound —
   * the scene is index-ordered (no element lacks one) and no other element
   * holds it. A repaired index kept on one screen is a private stacking
   * table: the next "send to back" is computed against it and means a
   * different place everywhere else (live class, 10-13). Only an index the
   * server itself has duplicated or left out stays repaired locally, since
   * taking that one back would just make the core repair it again.
   */
  const settleBookkeeping = () => {
    const settled: Array<
      Pick<SceneElement, "id" | "version" | "versionNonce" | "updated"> & {
        index?: string;
      }
    > = [];
    // undefined: not counted yet; null: some element has no index.
    let holders: Map<string, number> | null | undefined;
    const soundIndex = (stored: SceneElement, element: SceneElement) => {
      if (typeof stored.index !== "string" || stored.index === element.index) {
        return undefined;
      }
      if (holders === undefined) {
        holders = new Map();
        for (const entry of known.values()) {
          if (typeof entry.index !== "string") {
            holders = null;
            break;
          }
          holders.set(entry.index, (holders.get(entry.index) ?? 0) + 1);
        }
      }
      return holders?.get(stored.index) === 1 ? stored.index : undefined;
    };
    for (const element of options.scene.getElements()) {
      const stored = known.get(element.id);
      if (
        !stored ||
        hasPendingRevision(element.id) ||
        !isBookkeepingOnly(stored, slimTombstone(element))
      ) {
        continue;
      }
      const index = soundIndex(stored, element);
      if (!sameRevision(stored, element) || index !== undefined) {
        settled.push({
          id: element.id,
          version: stored.version,
          versionNonce: stored.versionNonce,
          updated: stored.updated,
          ...(index === undefined ? {} : { index }),
        });
      }
    }
    if (settled.length > 0) {
      options.scene.adoptRevisions(settled);
    }
  };

  /** Set while a re-check of the scene is scheduled (see below). */
  let cancelRecheck: (() => void) | null = null;

  const restoreUnchanged = (
    replacements: ReadonlyArray<{ sent: SceneElement; shown: SceneElement }>,
    placeByIndex = false,
  ) => {
    const local = new Map(
      options.scene.getElements().map((element) => [element.id, element]),
    );
    let skipped = false;
    const restored = replacements
      .filter(({ sent, shown }) => {
        const current = local.get(sent.id);
        if (current !== undefined && sameRevision(current, sent)) {
          return true;
        }
        if (current !== undefined && !sameRevision(current, shown)) {
          skipped = true;
        }
        return false;
      })
      .map(({ shown }) => shown);
    if (restored.length > 0) {
      if (placeByIndex) {
        options.scene.restore(restored, true);
      } else {
        options.scene.restore(restored);
      }
      settleBookkeeping();
    }
    // The core moved on past the refused revision (a drag still going, an
    // image still loading in place) so it could not be put back yet. The
    // core may never report the element again — an image finishing its load
    // mutates it silently — which left refused elements on this screen
    // alone (live class, 09-28). Look again shortly: the newer revision is
    // judged like any edit, and snaps back once it holds still.
    if (skipped && !cancelRecheck) {
      cancelRecheck = setTimer(() => {
        cancelRecheck = null;
        if (!disposed) {
          localChange(
            (options.scene.getEditable ?? options.scene.getElements)(),
          );
        }
      }, RECHECK_MILLISECONDS);
    }
  };

  const transmit = () => {
    const current = inFlight;
    if (!current || !canSend()) {
      return;
    }
    options.send(current.mutation).then(
      (ack) => {
        if (inFlight === current && !disposed) {
          handleAck(current, ack);
        }
      },
      () => {
        if (inFlight === current && !disposed) {
          scheduleRetry(current);
        }
      },
    );
  };

  const scheduleRetry = (current: InFlight) => {
    retrying = true;
    publish();
    cancelRetry?.();
    const delay = retryDelay(current.attempt);
    current.attempt += 1;
    cancelRetry = setTimer(() => {
      cancelRetry = null;
      if (inFlight === current) {
        transmit();
      }
    }, delay);
  };

  const sendNext = () => {
    if (!canSend() || inFlight) {
      return;
    }
    let elements = splitQueue.shift();
    if (!elements) {
      if (buffer.size === 0) {
        return;
      }
      elements = [...buffer.values()];
      buffer = new Map();
    }
    inFlight = {
      mutation: {
        classId: options.classId,
        canvasId: options.canvasId,
        epoch,
        baseVersion: version,
        clientId: options.clientId,
        mutationId: randomUuid(),
        elements,
      },
      elements: new Map(elements.map((element) => [element.id, element])),
      attempt: 0,
    };
    publish();
    transmit();
  };

  const scheduleSend = () => {
    if (
      !canSend() ||
      inFlight ||
      (buffer.size === 0 && splitQueue.length === 0)
    ) {
      return;
    }
    if (sendDelay <= 0) {
      sendNext();
      return;
    }
    cancelSend ??= setTimer(() => {
      cancelSend = null;
      sendNext();
    }, sendDelay);
  };

  const adoptEpoch = (canonical: CanonicalScene) => {
    const discarded = hasPendingWork();
    discardPendingWork();
    epoch = canonical.epoch;
    version = canonical.version;
    mode = canonical.mode;
    remember(canonical.scene);
    shown.clear();
    deletionDeclined.clear();
    options.scene.replace(canonical.scene);
    settleBookkeeping();
    if (discarded) {
      options.onNotice("EPOCH_DISCARDED");
    }
    publish();
  };

  const stop = (code: ErrorCode) => {
    revoked = true;
    cancelTimers();
    publish();
    return code;
  };

  /**
   * Every committed version is broadcast before its acknowledgement, so a
   * version this client never received (a commit behind an unclear COMMIT)
   * means its scene is incomplete: rejoin to load the canonical scene.
   * Unsaved work stays buffered and is sent after the join.
   */
  const requireResync = (
    reason: "version_gap" | "epoch_mismatch" | "version_skip" | "hash_mismatch",
  ) => {
    if (!joined) {
      return;
    }
    // Any pending change now waits on a base version the client hasn't
    // caught up to yet, rather than on the socket itself. The original,
    // coarser callback fires for every resync reason, same as before this
    // reason parameter existed; the richer one below carries which.
    if (hasUnsavedChanges()) {
      options.onDiagnostic?.("version_gap");
    }
    emitDiagnostic("resync", { reason });
    joined = false;
    publish();
    options.onResyncRequired();
  };

  const handleAck = (current: InFlight, ack: MutationAck) => {
    if (ack.ok) {
      inFlight = null;
      retrying = false;
      cancelRetry?.();
      cancelRetry = null;
      if (ack.epoch === epoch && ack.version > version) {
        // The lost-revision check below needs the versions this client missed.
        requireResync("version_gap");
        return;
      }
      const lost: Array<{ sent: SceneElement; shown: SceneElement }> = [];
      let declined = 0;
      for (const element of current.mutation.elements) {
        const existing = known.get(element.id);
        if (
          element.isDeleted &&
          ack.epoch === epoch &&
          existing &&
          !sameRevision(existing, element) &&
          !keepsExisting(existing, element)
        ) {
          // Every commit is broadcast to the room before its acknowledgement,
          // and this ack is not ahead of `version`, so a deletion the server
          // stored is already in `known`. One still missing was declined:
          // the server's guard drops a deletion of an element with nothing
          // in it (normalizationGuard.ts) and acknowledges the rest. Keeping
          // the sent tombstone anyway made this client's scene hash differ
          // from the server's until a full rejoin — and the next such
          // deletion broke it again (live class, 10-26: 79 mismatches).
          lost.push({ sent: element, shown: existing });
          deletionDeclined.add(element.id);
          declined += 1;
        } else if (!existing || !keepsExisting(existing, element)) {
          known.set(element.id, element);
          touchedDuringRepair?.add(element.id);
        } else if (!sameRevision(existing, element)) {
          // The server kept a newer revision and broadcast nothing new.
          lost.push({ sent: element, shown: existing });
        }
      }
      if (declined > 0) {
        emitDiagnostic("deletion_declined", { count: declined });
      }
      // The newer revision may be the server's own: it moves an element off
      // a stacking index another element holds, and the element has to sit
      // where the new index says or this screen stacks it its own way.
      restoreUnchanged(lost, true);
      publish();
      scheduleSend();
      return;
    }
    const { code } = ack;
    if (code === "STALE_EPOCH") {
      if (ack.canonical) {
        adoptEpoch(ack.canonical);
        scheduleSend();
      } else {
        joined = false;
        options.onResyncRequired();
      }
      return;
    }
    if (RETRYABLE.has(code)) {
      scheduleRetry(current);
      return;
    }
    if (REAUTHENTICATE.has(code)) {
      joined = false;
      retrying = true;
      publish();
      options.onReauthenticate();
      return;
    }
    if (REJECTED.has(code)) {
      inFlight = null;
      retrying = false;
      const { elements } = current.mutation;
      if (elements.length > 1) {
        // Isolate the refused elements so the rest of the batch is kept.
        const middle = Math.ceil(elements.length / 2);
        splitQueue.unshift(elements.slice(0, middle), elements.slice(middle));
      } else {
        // Cancel the refused edit on screen.
        restoreUnchanged(
          elements.map((element) => ({
            sent: element,
            shown: known.get(element.id) ?? { ...element, isDeleted: true },
          })),
        );
        noteRefusals(elements.map((element) => element.id));
        // FORBIDDEN is the frame policy declining an edit the student was
        // never meant to make (another student's frame). Defend silently:
        // the edit snaps back and nothing interrupts the lesson.
        if (code !== "FORBIDDEN") {
          options.onNotice("CHANGES_REJECTED");
        }
      }
      publish();
      scheduleSend();
      return;
    }
    options.onAccessLost(stop(code));
  };

  const inFlightElement = (id: string) => inFlight?.elements.get(id);

  const queuedElement = (id: string) => {
    for (let index = splitQueue.length - 1; index >= 0; index -= 1) {
      const found = splitQueue[index].find((element) => element.id === id);
      if (found) {
        return found;
      }
    }
    return undefined;
  };

  const latest = (id: string) =>
    buffer.get(id) ?? queuedElement(id) ?? inFlightElement(id) ?? known.get(id);

  const awaitsFile = (element: SceneElement) =>
    element.type === "image" &&
    !element.isDeleted &&
    typeof element.fileId === "string" &&
    !options.isFileReady(element.fileId);

  const localChange = (elements: readonly SceneElement[]) => {
    if (disposed || revoked || mode === "READ_ONLY") {
      return;
    }
    const present = new Set<string>();
    // Frames only change through the teacher, so the canonical scene's
    // frames are the ones every local edit is judged against.
    const verdict = options.student
      ? studentChangeVerdict(known.values(), options.student.studentNumber, {
          presentation: options.student.presentation,
        })
      : null;
    const mayChange = verdict
      ? (before: SceneElement | undefined, after: SceneElement) =>
          verdict(before, after) === null
      : null;
    const refused: Array<{ sent: SceneElement; shown: SceneElement }> = [];
    const reasons: StudentRefusal[] = [];
    // The core's eraser deletes a frame *together with everything in it* as
    // soon as a stroke touches the frame's border or name. The frame comes
    // back as a refused edit, but the student's own drawings were theirs to
    // delete and stayed deleted — a whole frame's work gone at one touch
    // (live class, 10-13). What a refused frame deletion took with it comes
    // back with the frame.
    const framesKept = new Set<string>();
    if (verdict) {
      for (const element of elements) {
        const reference = latest(element.id);
        if (
          element.isDeleted &&
          isFrameElement(element) &&
          reference &&
          !reference.isDeleted &&
          verdict(reference, element)
        ) {
          framesKept.add(element.id);
        }
      }
    }
    for (const local of elements) {
      // What is sent and kept as the server's copy; this screen keeps its
      // own full copy of a deleted element (see `slimTombstone`).
      const element = slimTombstone(local);
      present.add(element.id);
      shown.add(element.id);
      const reference = latest(element.id);
      if (
        !heldBack.has(element.id) &&
        reference &&
        sameRevision(reference, element)
      ) {
        continue;
      }
      if (!reference && element.isDeleted) {
        // Never stored, so there is nothing to delete.
        heldBack.delete(element.id);
        continue;
      }
      if (
        element.isDeleted &&
        reference &&
        !reference.isDeleted &&
        isNothingYet(element) &&
        !authored.has(element.id)
      ) {
        // The core tidies away an element that has nothing in it yet — a
        // shape at the click before the drag, a label before its first
        // keystroke. On the author's screen that is their own doing, but
        // on every *other* screen it is normalization of something still
        // being made, and sending it deleted the author's work for the
        // whole class (live class, 09-23). The server refuses these too;
        // not sending them keeps this editor from diverging in the
        // meantime.
        continue;
      }
      if (
        element.isDeleted &&
        deletionDeclined.has(element.id) &&
        isNothingYet(element)
      ) {
        continue;
      }
      const sweptWithFrame =
        element.isDeleted &&
        reference &&
        !reference.isDeleted &&
        typeof reference.frameId === "string" &&
        framesKept.has(reference.frameId);
      const refusal = verdict
        ? verdict(reference, element) ?? (sweptWithFrame ? "frame" : null)
        : null;
      if (refusal) {
        // Judged against the last revision this client accepted, so a
        // refused drag stops where it last was allowed, not where it began.
        reasons.push(refusal);
        if (isOwnEdit(reference, element)) {
          options.onRefused?.(refusal);
        }
        heldBack.delete(element.id);
        refused.push({
          sent: element,
          shown: reference ?? { ...element, isDeleted: true },
        });
        continue;
      }
      if (awaitsFile(element)) {
        heldBack.add(element.id);
        continue;
      }
      heldBack.delete(element.id);
      if (!known.has(element.id)) {
        authored.add(element.id);
      }
      // A detached copy: Excalidraw mutates element objects in place (for
      // example setting an image's file ID), which must not rewrite a
      // revision already queued or sent.
      buffer.set(element.id, structuredClone(element));
    }
    // A scene replacement (such as opening a file) drops elements outright;
    // the server only learns of that through higher-version tombstones.
    // Only elements this editor has actually had in its scene count: the
    // core's `restoreElements` silently drops invisibly small elements on
    // arrival (a peer's shape or stroke in its first moments), and treating
    // those as local deletions deleted a classmate's fresh drawing for
    // everyone.
    for (const id of shown) {
      const reference = latest(id);
      if (present.has(id)) {
        continue;
      }
      if (!reference || reference.isDeleted) {
        shown.delete(id);
        continue;
      }
      let tombstone = slimTombstone({
        ...reference,
        isDeleted: true,
        version: reference.version + 1,
        versionNonce: randomNonce(),
        updated: now(),
      });
      if (mayChange && !mayChange(reference, tombstone)) {
        continue;
      }
      // H5: only on the teacher's own editor (`options.student` is only set
      // for a student) — a student's own vanished element is an ordinary
      // deletion, not teacher material.
      if (!options.student) {
        tombstone = withTeacherDeletionMark(tombstone);
      }
      buffer.set(id, tombstone);
      shown.delete(id);
    }
    for (const id of heldBack) {
      if (!present.has(id)) {
        heldBack.delete(id);
      }
    }
    restoreUnchanged(refused);
    noteRefusals(
      refused.map(({ sent }) => sent.id),
      reasons,
    );
    publish();
    scheduleSend();
  };

  /**
   * Elements this client believes match the server exactly (same version
   * *and* versionNonce as `known`) but whose content differs — a scene the
   * core silently re-normalized after the fact. Reported, never resynced:
   * unlike a hash mismatch, there is no evidence *which* copy is right.
   */
  /** Fields the core re-derives on load without it being anyone's edit:
   * `lastCommittedPoint` is transient drawing state, and a line's or
   * arrow's size is recomputed from its points (`restoreElements`), so a
   * stored size that disagrees with identical points is not a divergence —
   * one such deleted line made every open editor report it every 15s
   * (live class, 10-19). The core also moves a first point that is not at
   * [0, 0] there and shifts x/y to match, so every point stays where it was
   * on the canvas: the sample canvas's bound arrows start at [0, -0.5] and
   * made each teacher who opened it report all seven (10-21~23).
   * Empty-vs-null and index/revision differences are already ignored by
   * isBookkeepingOnly. */
  const withoutCoreNormalization = (
    stored: SceneElement,
    element: SceneElement,
  ): SceneElement => {
    const normalized: SceneElement = {
      ...element,
      lastCommittedPoint: stored.lastCommittedPoint,
    };
    if (!Array.isArray(element.points)) {
      return normalized;
    }
    if (canonicalJson(stored.points) === canonicalJson(element.points)) {
      normalized.width = stored.width;
      normalized.height = stored.height;
    } else if (sameAbsolutePoints(stored, element)) {
      normalized.x = stored.x;
      normalized.y = stored.y;
      normalized.points = stored.points;
      normalized.width = stored.width;
      normalized.height = stored.height;
    }
    return normalized;
  };

  /** Revisions (`id:version:nonce`) already reported as diverged: the same
   * one is reported once, not again on every verify round. */
  const reportedDrift = new Set<string>();

  const checkNormalizationDrift = () => {
    let diverged = 0;
    const keys = new Set<string>();
    const unsent = new Set([
      ...buffer.keys(),
      ...splitQueue.flat().map((element) => element.id),
      ...(inFlight?.elements.keys() ?? []),
    ]);
    for (const local of options.scene.getElements()) {
      const element = slimTombstone(local);
      const stored = known.get(element.id);
      if (
        stored &&
        sameRevision(stored, element) &&
        !unsent.has(element.id) &&
        !isBookkeepingOnly(stored, withoutCoreNormalization(stored, element))
      ) {
        const revision = `${element.id}:${element.version}:${element.versionNonce}`;
        if (reportedDrift.has(revision)) {
          continue;
        }
        reportedDrift.add(revision);
        diverged += 1;
        for (const key of new Set([
          ...Object.keys(stored),
          ...Object.keys(element),
        ])) {
          if (
            canonicalJson(stored[key as keyof SceneElement]) !==
            canonicalJson(element[key as keyof SceneElement])
          ) {
            keys.add(key);
          }
        }
      }
    }
    if (diverged > 0) {
      emitDiagnostic("scene_diverged", {
        count: diverged,
        keys: [...keys].slice(0, 20),
      });
    }
  };

  /** H8: makes the server's copy win outright on the next join, bypassing
   * the normal by-version reconcile. Throttled — a forced resync discards
   * local work, and repeatedly forcing it would make a persistent mismatch
   * (a bug elsewhere) far more disruptive than reporting it once. */
  const maybeForceResync = () => {
    const nowMs = now();
    if (
      lastForcedResyncAt !== null &&
      nowMs - lastForcedResyncAt < FORCED_RESYNC_MIN_INTERVAL_MILLISECONDS
    ) {
      return;
    }
    lastForcedResyncAt = nowMs;
    forceServerWinsOnNextJoin = true;
    requireResync("hash_mismatch");
  };

  /**
   * Hashes the scene this client believes is canonical (`known`, the same
   * elements it would send nothing further about) and compares it with the
   * server's hash for the version just confirmed to match. Async: by the
   * time the digest finishes, local state may have moved on, so every use
   * of `atVersion`/`atEpoch` is re-checked against the live `version`/
   * `epoch` before acting on the result.
   */
  const checkHash = async (
    serverHash: string,
    atVersion: number,
    atEpoch: string,
  ) => {
    const startedAt = now();
    const scene = [...known.values()];
    const canonical = canonicalJson(orderScene(scene));
    let digest: ArrayBuffer;
    try {
      digest = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(canonical),
      );
    } catch {
      // No WebCrypto (very old browser) — verification simply can't run.
      return;
    }
    lastHashMilliseconds = now() - startedAt;
    if (disposed || version !== atVersion || epoch !== atEpoch) {
      // State moved during hashing; the next verify window judges the new
      // state instead of comparing it against this stale snapshot.
      return;
    }
    if (toHex(digest) === serverHash) {
      return;
    }
    emitDiagnostic("hash_mismatch", {
      version: atVersion,
      elements: scene.length,
    });
    if (
      options.repair &&
      (lastRepairAt === null ||
        now() - lastRepairAt >= REPAIR_MIN_INTERVAL_MILLISECONDS)
    ) {
      repair(options.repair);
      return;
    }
    maybeForceResync();
  };

  /**
   * Fetches only the elements whose server copy differs from `known`, and
   * lets each win outright as a forced resync would (H8) — instead of
   * rejoining for the whole scene, which on a 6 MB class canvas was a 6 MB
   * download per mismatch per tablet (live class, 10-26). Anything that
   * does not work out falls back to that rejoin.
   */
  const repair = (
    request: (request: CanvasRepairRequest) => Promise<CanvasRepairAck>,
  ) => {
    lastRepairAt = now();
    repairInFlight = true;
    const touched = new Set<string>();
    touchedDuringRepair = touched;
    const manifest: Array<[string, string]> = [...known.values()].map(
      (element) => [element.id, digestOf(element)],
    );
    request({ canvasId: options.canvasId, manifest }).then(
      (ack) => {
        repairInFlight = false;
        touchedDuringRepair = null;
        if (disposed || !joined) {
          // A rejoin is already on its way and loads the whole scene.
          return;
        }
        if (!ack.ok || ack.epoch !== epoch) {
          maybeForceResync();
          return;
        }
        const pending = pendingIds();
        const repaired: SceneElement[] = [];
        for (const element of ack.elements) {
          if (touched.has(element.id)) {
            // Updated since the manifest was taken, by a commit the answer
            // may predate. Anything else is the server's, whatever version
            // this client holds: an inflated local version is exactly what
            // a repair is for.
            continue;
          }
          known.set(element.id, element);
          if (!pending.has(element.id)) {
            repaired.push(element);
          }
        }
        for (const id of ack.missing) {
          if (!pending.has(id)) {
            known.delete(id);
          }
        }
        // The answer is the server's scene at its version, so the commits
        // up to it that this client has not heard yet are already in it.
        version = Math.max(version, ack.version);
        if (repaired.length > 0) {
          options.scene.restore(differingOnScreen(repaired));
          options.scene.reconcile(repaired);
        }
        settleBookkeeping();
        emitDiagnostic("scene_repaired", {
          elements: ack.elements.length,
          missing: ack.missing.length,
        });
        // The next round checks the repaired scene, whatever the version.
        lastVerifiedVersion = null;
        publish();
      },
      () => {
        repairInFlight = false;
        touchedDuringRepair = null;
        if (!disposed) {
          maybeForceResync();
        }
      },
    );
  };

  /** Minimum and maximum time between verify rounds for this scene and
   * this device. */
  const verifyIntervals = () => {
    const stretch = Math.min(
      VERIFY_MAX_STRETCH,
      Math.max(1, known.size / VERIFY_ELEMENTS_PER_STRETCH),
    );
    const minimum = Math.max(
      VERIFY_MIN_INTERVAL_MILLISECONDS * stretch,
      lastHashMilliseconds / VERIFY_MAX_DUTY,
    );
    return {
      minimum,
      maximum: Math.max(minimum, VERIFY_MAX_INTERVAL_MILLISECONDS * stretch),
    };
  };

  const dueForVerify = () =>
    Boolean(options.verify) &&
    joined &&
    !hasPendingWork() &&
    !verifyInFlight &&
    !repairInFlight &&
    (() => {
      const elapsed = now() - (lastVerifyAttemptAt ?? -Infinity);
      const { minimum, maximum } = verifyIntervals();
      if (elapsed < minimum) {
        return false;
      }
      return version !== lastVerifiedVersion || elapsed >= maximum;
    })();

  const maybeVerify = () => {
    if (!dueForVerify()) {
      return;
    }
    checkNormalizationDrift();
    verifyInFlight = true;
    lastVerifyAttemptAt = now();
    const atVersion = version;
    const atEpoch = epoch;
    options.verify!(options.canvasId).then(
      (ack) => {
        verifyInFlight = false;
        if (
          disposed ||
          !ack.ok ||
          ack.epoch !== atEpoch ||
          ack.epoch !== epoch ||
          ack.version !== atVersion ||
          ack.version !== version
        ) {
          // Refused, or the local state moved during the round trip — skip
          // this round; the next window re-evaluates from scratch.
          return;
        }
        lastVerifiedVersion = ack.version;
        void checkHash(ack.sceneHash, atVersion, atEpoch);
      },
      () => {
        // Network hiccup, or an old server that doesn't know the event —
        // both fail quietly (see `CollaborationTransport.verify`).
        verifyInFlight = false;
      },
    );
  };

  const scheduleVerifyPoll = () => {
    if (!options.verify || disposed) {
      return;
    }
    cancelVerifyPoll = setTimer(() => {
      cancelVerifyPoll = null;
      maybeVerify();
      scheduleVerifyPoll();
    }, options.verifyPollMilliseconds ?? DEFAULT_VERIFY_POLL_MILLISECONDS);
  };
  scheduleVerifyPoll();

  return {
    localChange,
    revisionOf: (elementId) => latest(elementId),
    joined(result) {
      if (disposed) {
        return;
      }
      joined = true;
      if (result.epoch !== epoch) {
        forceServerWinsOnNextJoin = false;
        adoptEpoch(result);
        scheduleSend();
        return;
      }
      mode = result.mode;
      version = result.version;
      if (forceServerWinsOnNextJoin) {
        // H8: a plain same-epoch join reconciles by version below, so a
        // locally corrupted element with an inflated version would survive
        // even a rejoin. After a verified hash mismatch the server's copy
        // wins outright — except for elements with unsent local work, which
        // is kept and sent as usual (nothing the user did is thrown away).
        forceServerWinsOnNextJoin = false;
        const pending = pendingIds();
        options.scene.restore(
          differingOnScreen(
            result.scene.filter((element) => !pending.has(element.id)),
          ),
        );
      }
      remember(result.scene);
      options.scene.reconcile(result.scene);
      // The editor normalized the scene it opened with before this join;
      // that too is bookkeeping, not work to send.
      for (const [id, element] of buffer) {
        const stored = known.get(id);
        if (stored && isBookkeepingOnly(stored, element)) {
          buffer.delete(id);
        }
      }
      settleBookkeeping();
      if (mode === "READ_ONLY") {
        const discarded = hasPendingWork();
        discardPendingWork();
        if (discarded) {
          options.onNotice("READ_ONLY_DISCARDED");
        }
        publish();
        return;
      }
      if (inFlight && !revoked) {
        cancelRetry?.();
        cancelRetry = null;
        transmit();
      }
      publish();
      scheduleSend();
    },
    disconnected() {
      if (hasUnsavedChanges()) {
        options.onDiagnostic?.("reconnecting");
        emitDiagnostic("reconnecting", {
          buffer: buffer.size,
          splitQueue: splitQueue.length,
          inFlight: inFlight ? 1 : 0,
        });
      }
      joined = false;
      publish();
    },
    canonical(update) {
      if (disposed || update.canvasId !== options.canvasId) {
        return;
      }
      if (update.epoch !== epoch) {
        // A broadcast carries only one commit's elements; it is the whole scene
        // only for the restore itself, which this client may have missed. The
        // join returns the canonical scene and discards work of the old epoch.
        requireResync("epoch_mismatch");
        return;
      }
      if (update.version <= version) {
        return;
      }
      if (update.version > version + 1) {
        requireResync("version_skip");
        return;
      }
      version = update.version;
      for (const element of update.elements) {
        known.set(element.id, element);
        touchedDuringRepair?.add(element.id);
      }
      options.scene.reconcile(update.elements);
      settleBookkeeping();
      publish();
    },
    revoke(code) {
      stop(code);
    },
    hasUnsavedChanges,
    status,
    debug: () => ({
      epoch,
      version,
      pending: {
        buffer: buffer.size,
        splitQueue: splitQueue.length,
        inFlight: inFlight ? 1 : 0,
        heldBack: heldBack.size,
      },
      knownDigest: async () => {
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(
            canonicalJson(orderScene([...known.values()])),
          ),
        );
        return toHex(digest);
      },
      verifyNow: async () => {
        if (!options.verify) {
          return { error: "verify not configured" };
        }
        try {
          return await options.verify(options.canvasId);
        } catch (error) {
          return {
            error: error instanceof Error ? error.message : String(error),
          };
        }
      },
    }),
    dispose() {
      disposed = true;
      cancelTimers();
      cancelRefusalFlush?.();
      cancelRefusalFlush = null;
      cancelRecheck?.();
      cancelRecheck = null;
      cancelStuckSaving?.();
      cancelStuckSaving = null;
      cancelVerifyPoll?.();
      cancelVerifyPoll = null;
    },
  };
};
